/** Professional revenue report HTML for print / PDF. */

function esc(s) {
    return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function inr(n) {
    return `₹${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
    return new Date(iso).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
    });
}

function fmtDateTime(iso) {
    return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
    });
}

const MODE_LABELS = {
    cash: "Cash", upi: "UPI", card: "Card", razorpay: "Razorpay", wallet: "Wallet",
};

function tableSection(title, headers, rows) {
    if (!rows) return "";
    return `
        <section class="section">
            <h2 class="section-title">${esc(title)}</h2>
            <table>
                <thead><tr>${headers.map((h) => `<th${h.align ? ` style="text-align:${h.align}"` : ""}>${esc(h.label)}</th>`).join("")}</tr></thead>
                <tbody>${rows}</tbody>
            </table>
        </section>`;
}

/**
 * @param {object} data — /reports/revenue response
 * @param {object} business — { name, address, city, phone, gstin }
 */
export function buildReportPrintHtml(data, business = {}) {
    const bizName = business.name || "Salon & Spa";
    const addressLine = [business.address, business.city].filter(Boolean).join(", ");
    const phone = business.phone?.trim();
    const gstin = business.gstin?.trim();
    const avgTicket = data.count ? data.total / data.count : 0;
    const growth = data.growth_pct != null
        ? `${data.growth_pct >= 0 ? "+" : ""}${data.growth_pct}% vs prior period`
        : "No prior period data";

    const kpiCards = [
        ["Total Revenue", inr(data.total)],
        ["Invoices", String(data.count)],
        ["GST Collected", inr(data.gst_total)],
        ["Membership Collected", inr(data.membership_collected_in_period)],
        ["Outstanding Dues", inr(data.membership_outstanding_total)],
        ["Avg Ticket", inr(avgTicket)],
        ["New Customers", String(data.new_customers)],
        ["Labor Cost", inr(data.labor_cost_total)],
        ["Labor %", data.labor_cost_pct != null ? `${data.labor_cost_pct}%` : "—"],
    ].map(([k, v]) => `
        <div class="kpi"><span class="kpi-k">${esc(k)}</span><span class="kpi-v">${v}</span></div>`).join("");

    const dayRows = (data.by_day || [])
        .filter((d) => d.revenue > 0 || (data.by_day_invoices?.find((x) => x.date === d.date)?.count > 0))
        .map((d) => {
            const inv = data.by_day_invoices?.find((x) => x.date === d.date);
            return `<tr><td>${esc(d.date)}</td><td class="num">${inr(d.revenue)}</td><td class="num">${inv?.count ?? 0}</td></tr>`;
        }).join("") || '<tr><td colspan="3" class="empty">No daily data</td></tr>';

    const payRows = (data.by_payment || []).map((p) =>
        `<tr><td>${esc(MODE_LABELS[p.mode] || p.mode)}</td><td class="num">${inr(p.amount)}</td></tr>`
    ).join("") || '<tr><td colspan="2" class="empty">No payments</td></tr>';

    const svcRows = (data.by_service || []).map((s) =>
        `<tr><td>${esc(s.name)}</td><td class="num">${s.count}</td><td class="num">${inr(s.revenue)}</td></tr>`
    ).join("") || '<tr><td colspan="3" class="empty">No service data</td></tr>';

    const staffRows = (data.staff_payroll || []).map((s) =>
        `<tr>
            <td>${esc(s.name)}</td>
            <td>${esc(s.role)}</td>
            <td>${esc(s.pay_type)}</td>
            <td class="num">${inr(s.revenue)}</td>
            <td class="num">${inr(s.base_salary)}</td>
            <td class="num">${inr(s.commission)}</td>
            <td class="num">${inr(s.total_earnings)}</td>
        </tr>`
    ).join("") || '<tr><td colspan="7" class="empty">No payroll data</td></tr>';

    const staffRowsLegacy = (data.by_staff || []).map((s) =>
        `<tr><td>${esc(s.name)}</td><td class="num">${inr(s.amount)}</td></tr>`
    ).join("") || '<tr><td colspan="2" class="empty">No staff data</td></tr>';

    const typeRows = (data.by_revenue_type || []).map((t) =>
        `<tr><td>${esc(t.label)}</td><td class="num">${inr(t.amount)}</td></tr>`
    ).join("") || '<tr><td colspan="2" class="empty">No category data</td></tr>';

    const outstandingRows = (data.membership_outstanding || []).map((r) =>
        `<tr>
            <td>${esc(r.customer_name)}</td>
            <td>${esc(r.plan_name)}</td>
            <td class="num">${inr(r.total_due)}</td>
            <td class="num">${inr(r.total_paid)}</td>
            <td class="num">${inr(r.balance_due)}</td>
        </tr>`
    ).join("") || '<tr><td colspan="5" class="empty">No outstanding membership dues</td></tr>';

    const ledgerRows = (data.ledger || []).map((r) =>
        `<tr>
            <td>${esc(r.invoice_number)}</td>
            <td>${esc(r.sale_type_label || r.sale_type || "—")}</td>
            <td>${esc(r.customer_name)}</td>
            <td>${esc(r.customer_mobile || "—")}</td>
            <td>${esc(fmtDateTime(r.created_at))}</td>
            <td>${esc(MODE_LABELS[r.payment_mode] || r.payment_mode)}</td>
            <td class="num">${inr(r.grand_total)}</td>
        </tr>`
    ).join("") || '<tr><td colspan="7" class="empty">No invoices</td></tr>';

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <title>Revenue Report — ${esc(data.period_start)} to ${esc(data.period_end)}</title>
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @page { size: A4 portrait; margin: 14mm 12mm; }
        body {
            font-family: "Segoe UI", system-ui, sans-serif;
            font-size: 11px;
            line-height: 1.45;
            color: #1a1a1a;
            background: #fff;
        }
        .page { max-width: 186mm; margin: 0 auto; }

        /* Header */
        .head {
            text-align: center;
            padding-bottom: 12px;
            margin-bottom: 14px;
            border-bottom: 2px solid #4A0E17;
        }
        .biz-name {
            font-size: 18px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: #4A0E17;
        }
        .biz-meta { font-size: 10px; color: #555; margin-top: 4px; }
        .doc-title {
            margin-top: 10px;
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.22em;
            text-transform: uppercase;
            color: #333;
        }
        .period-bar {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            gap: 8px;
            margin: 12px 0 16px;
            padding: 10px 12px;
            background: #FAF9F6;
            border: 1px solid #E8E4DE;
            border-radius: 4px;
            font-size: 10px;
        }
        .period-bar strong { color: #4A0E17; }

        /* KPIs */
        .kpis {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 8px;
            margin-bottom: 18px;
        }
        .kpi {
            border: 1px solid #E8E4DE;
            border-radius: 4px;
            padding: 8px 10px;
            background: #FFFEF9;
        }
        .kpi-k {
            display: block;
            font-size: 8px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: #888;
        }
        .kpi-v {
            display: block;
            margin-top: 3px;
            font-size: 13px;
            font-weight: 800;
            color: #3D2817;
        }

        /* Sections */
        .section { margin-bottom: 18px; page-break-inside: avoid; }
        .section-title {
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.12em;
            color: #4A0E17;
            padding-bottom: 6px;
            margin-bottom: 8px;
            border-bottom: 1px solid #E8E4DE;
        }
        table { width: 100%; border-collapse: collapse; font-size: 10px; }
        th {
            background: #FAF9F6;
            border-top: 1px solid #333;
            border-bottom: 1px solid #333;
            padding: 6px 8px;
            font-size: 9px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            text-align: left;
        }
        td {
            padding: 5px 8px;
            border-bottom: 1px dotted #ddd;
            vertical-align: top;
        }
        td.num, th[style*="right"] { text-align: right; white-space: nowrap; }
        td.empty { text-align: center; color: #999; padding: 12px; }

        .two-col {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
        }

        /* Footer */
        .foot {
            margin-top: 20px;
            padding-top: 12px;
            border-top: 1px dashed #999;
            text-align: center;
            font-size: 9px;
            color: #666;
            line-height: 1.6;
        }
        .foot-note { font-weight: 600; color: #4A0E17; margin-bottom: 4px; }

        @media print {
            .page { max-width: 100%; }
            .section { page-break-inside: auto; }
        }
    </style>
</head>
<body>
    <div class="page">
        <header class="head">
            <div class="biz-name">${esc(bizName)}</div>
            ${addressLine ? `<div class="biz-meta">${esc(addressLine)}</div>` : ""}
            ${phone || gstin ? `<div class="biz-meta">${phone ? `Tel: ${esc(phone)}` : ""}${phone && gstin ? " · " : ""}${gstin ? `GSTIN: ${esc(gstin)}` : ""}</div>` : ""}
            <div class="doc-title">Revenue Report</div>
        </header>

        <div class="period-bar">
            <span><strong>Period:</strong> ${esc(data.period_start)} → ${esc(data.period_end)} (${data.period_days} days)</span>
            <span><strong>Growth:</strong> ${esc(growth)}</span>
            <span><strong>Generated:</strong> ${esc(fmtDateTime(new Date().toISOString()))}</span>
        </div>

        <div class="kpis">${kpiCards}</div>

        <div class="two-col">
            ${tableSection("Revenue by category", [
                { label: "Category" }, { label: "Amount", align: "right" },
            ], typeRows)}
            ${tableSection("Payment split", [
                { label: "Mode" }, { label: "Amount", align: "right" },
            ], payRows)}
        </div>

        <div class="two-col">
            ${tableSection("Top services", [
                { label: "Service" }, { label: "Qty", align: "right" }, { label: "Revenue", align: "right" },
            ], svcRows)}
            ${tableSection("Staff revenue", [
                { label: "Stylist" }, { label: "Revenue", align: "right" },
            ], staffRowsLegacy)}
        </div>

        ${tableSection("Staff payroll", [
            { label: "Staff" }, { label: "Role" }, { label: "Pay" },
            { label: "Revenue", align: "right" }, { label: "Base", align: "right" },
            { label: "Commission", align: "right" }, { label: "Total", align: "right" },
        ], staffRows)}

        ${tableSection("Membership dues outstanding", [
            { label: "Customer" }, { label: "Plan" },
            { label: "Total", align: "right" }, { label: "Paid", align: "right" }, { label: "Balance", align: "right" },
        ], outstandingRows)}

        ${tableSection("Daily revenue", [
            { label: "Date" }, { label: "Revenue", align: "right" }, { label: "Invoices", align: "right" },
        ], dayRows)}

        <section class="section">
            <h2 class="section-title">Invoice ledger (${data.ledger?.length ?? 0} paid)</h2>
            <table>
                <thead>
                    <tr>
                        <th>Invoice</th>
                        <th>Type</th>
                        <th>Customer</th>
                        <th>Mobile</th>
                        <th>Date</th>
                        <th>Payment</th>
                        <th style="text-align:right">Amount</th>
                    </tr>
                </thead>
                <tbody>${ledgerRows}</tbody>
                <tfoot>
                    <tr>
                        <td colspan="6" style="text-align:right;font-weight:700;border-top:2px solid #333;padding-top:8px">Net collected</td>
                        <td class="num" style="font-weight:800;border-top:2px solid #333;padding-top:8px">${inr(data.total)}</td>
                    </tr>
                    ${(data.membership_outstanding_total || 0) > 0 ? `
                    <tr>
                        <td colspan="6" style="text-align:right;font-weight:700;padding-top:6px;color:#7A2438">Membership outstanding (receivable)</td>
                        <td class="num" style="font-weight:800;padding-top:6px;color:#C84B31">${inr(data.membership_outstanding_total)}</td>
                    </tr>` : ""}
                </tfoot>
            </table>
        </section>

        <footer class="foot">
            <div class="foot-note">Confidential — For internal &amp; accounting use only</div>
            <div>Sub-total ${inr(data.sub_total)} · GST ${inr(data.gst_total)} · Discounts ${inr(data.discount_total)} · Prior period ${inr(data.prev_period_total)}</div>
            <div>Membership collected ${inr(data.membership_collected_in_period)} · Contract value (period) ${inr(data.membership_contract_value)} · Outstanding ${inr(data.membership_outstanding_total)}</div>
            <div style="margin-top:6px">Computer-generated report. No signature required.</div>
        </footer>
    </div>
    <script>window.onload=function(){window.print();}<\/script>
</body>
</html>`;
}

export function openReportPrint(data, business = {}) {
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return false;
    w.document.write(buildReportPrintHtml(data, business));
    w.document.close();
    return true;
}
